import videojs from 'video.js'
import NFBHLSQualityItem from '../components/NFBHLSQualityItem.js'

const Plugin = videojs.getPlugin('plugin')

const defaults = {}

class NFBHLSQualitySelector extends Plugin {

  /**
   * Create a NFBHLSQualitySelector plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor(player, options) {
    // the parent class will add player under this.player
    super(player)

    this.options = videojs.obj.merge(defaults, options)
    this.levelItems = []

    this.onAddQualityLevel = this.onAddQualityLevel.bind(this)

    this.player.ready(() => {
      this.createQualityContainer()
      this.player.on('settingspanelready', () => {
        this.assignQualityItems()
      })

      player.on('sourcechanged', () => {
        this.qualityContainer?.remove()
        this.player.qualityLevels().off('addqualitylevel',this.onAddQualityLevel)
      })

      this.player.on('canplay', () => {
        // This is to check if the quality container already exists before creating one.
        // When the sourcechanged is triggered (mostly when DV button is clicked) we remove the quality container
        if (this.player.qualityLevels() && !this.player.el().querySelector('[data-ui-el="settings-panel-quality-hls"]')) {
          this.createQualityContainer()
          this.assignQualityItems()
        }
      })
    })
  }

  createQualityContainer() {
    this.qualityContainer = document.createElement('div')
    this.qualityContainer.classList.add('nfb-settings-panel__items-container')
    this.qualityContainer.setAttribute('data-ui-el', 'settings-panel-quality-hls')
    this.qualityContainer.setAttribute('data-cy', 'settings-panel-quality')
    this.qualityContainer.innerHTML = `<span class="nfb-settings-panel__item-title">${this.player.localize('Quality Selector')}</span>`
  
    this.bindPlayerEvents()
  }

  assignQualityItems() {
    this.player.el().querySelector('[data-ui-el="settings-panel"]').append(this.qualityContainer)

    this.levelItems.push(this.getQualityMenuItem.call(this, {
      label: this.player.localize('Auto'),
      value: 'auto',
      selected: true
    }))

    this.levelItems?.forEach((levelItem) => {
      this.qualityContainer.appendChild(levelItem.el())
    })
  }

  bindPlayerEvents() {
    this.player.qualityLevels().on('addqualitylevel', this.onAddQualityLevel)
  }

  // Builds individual quality menu items.
  getQualityMenuItem(item) {
    const player = this.player

    return new NFBHLSQualityItem(player, item, this.levelItems, this)
  }

  //Executed when a quality level is added from HLS playlist.
  onAddQualityLevel(event) {
    if (event.qualityLevel) {
      const { width, height } = event.qualityLevel
      const videoResolution = width > height ? height : width

      if (!videoResolution || this.options.disableResolutions.includes(videoResolution)) {
        return
      }

      // before creating NFBHLSQualityItem we check if it is unique
      if (!this.levelItems.find(_existingItem => {
        return _existingItem.item && _existingItem.item.value === videoResolution
      })) {
        const levelItem = this.getQualityMenuItem.call(this, {
          label: videoResolution + 'p',
          value: videoResolution
        })
        this.levelItems.push(levelItem)
      }
    }
  
    this.levelItems.sort((current, next) => {
      if ((typeof current !== 'object') || (typeof next !== 'object')) {
        return -1
      }
      if (current.item.value < next.item.value) {
        return -1
      }
      if (current.item.value > next.item.value) {
        return 1
      }
      return 0
    })

    return this.levelItems
  }

  setQuality(quality) {
    const qualityList = this.player.qualityLevels().levels_

    // Set quality on plugin
    this._currentQuality = quality

    qualityList.forEach((qualityItem) => {
      // the width and height of a quality level represents the horizontal and vertical resolutions
      const { width, height } = qualityItem
      const videoResolution = width > height ? height : width
  
      qualityItem.enabled = (videoResolution === quality || quality === 'auto')
    })
  }
}

NFBHLSQualitySelector.defaultState = {}

// Include the version number.
NFBHLSQualitySelector.VERSION = '1.0.0'

// Register the plugin with video.js.
videojs.registerPlugin('nfbHLSQualitySelector', NFBHLSQualitySelector)

export default NFBHLSQualitySelector
